import { Component, ViewEncapsulation, OnDestroy, AfterViewInit, OnInit } from '@angular/core';
import { IdentityService } from './services/identity.service';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Team } from './models/team';
import { TeamsService } from './services/teams.service';
import { ActivatedRoute, Router } from '@angular/router';
import { KeycloakEventTypeLegacy as KeycloakEventType, KeycloakService } from 'keycloak-angular';
import { ConfigService } from './services/config.service';
import { MenuService } from './services/menu.service';
import { MatDialog } from '@angular/material/dialog';
import { SettingsService } from './services/settings.service';
import { WidgetService } from './services/widget.service';
import { BaseWidgetComponent } from './components/strategy-grid/base-widget/base-widget.component';
import { HeartbeatService } from './services/heartbeat.service';
import { VersionService } from './services/version.service';
import { isNil } from 'lodash';
import { MenubarService } from './services/menubar.service';
import { UserPrefsService } from './services/user-prefs.service';
import { MuxUsageService } from './services/mux-usage.service';

const CHANGELOG_URL = 'https://docs.lit.ai/reference/changelog/';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  title = 'lit';
  team: Team | undefined = undefined;
  menuItems = [];
  subs: any[] = [];
  color: string;
  build: string;
  version: string | undefined;

  public get features(): any {
    return ConfigService.features || {};
  }

  public get hasClipboard(): boolean {
    return navigator.clipboard !== undefined;
  }

  public get initialized(): boolean {
    return ConfigService.initialized;
  }
  
  public get showMenubar(): boolean {
    return true;
  }

  public get changelogURL(): string {
    if (this.version !== undefined) {
      return `${CHANGELOG_URL}#${this.version}`;
    }
    return CHANGELOG_URL;
  }

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public identity: IdentityService,
    public menuService: MenuService,
    public dialog: MatDialog,
    private _matIconRegistry: MatIconRegistry,
    private _domSanitizer: DomSanitizer,
    private _teams: TeamsService,
    private _keycloak: KeycloakService,
    private _settings: SettingsService,
    private _widgets: WidgetService,
    private _heartbeatService: HeartbeatService,
    private _version: VersionService,
    private _menubarService: MenubarService,
    private _userPrefs: UserPrefsService,
    private _muxUsage: MuxUsageService
  ) {
    (<any>window).app = this;

    this._matIconRegistry.addSvgIcon('flame', this._domSanitizer.bypassSecurityTrustResourceUrl('assets/flame.svg'));
    this._matIconRegistry.addSvgIcon('deepseek', this._domSanitizer.bypassSecurityTrustResourceUrl('assets/model-icons/deepseek.svg'));
    this._matIconRegistry.addSvgIcon('ollama', this._domSanitizer.bypassSecurityTrustResourceUrl('assets/model-icons/ollama.svg'));
    this._matIconRegistry.addSvgIcon('llama', this._domSanitizer.bypassSecurityTrustResourceUrl('assets/model-icons/ollama.svg'));
    this._matIconRegistry.addSvgIcon('mistral', this._domSanitizer.bypassSecurityTrustResourceUrl('assets/model-icons/mistral.svg'));
    this._matIconRegistry.addSvgIcon('qwen', this._domSanitizer.bypassSecurityTrustResourceUrl('assets/model-icons/qwen.svg'));
    this._matIconRegistry.addSvgIcon('microsoft', this._domSanitizer.bypassSecurityTrustResourceUrl('assets/model-icons/microsoft.svg'));
    this._matIconRegistry.addSvgIcon('data', this._domSanitizer.bypassSecurityTrustResourceUrl('assets/data.svg'));
    this._matIconRegistry.addSvgIcon('beaker', this._domSanitizer.bypassSecurityTrustResourceUrl('assets/beaker.svg'));
    this._matIconRegistry.addSvgIcon('audit', this._domSanitizer.bypassSecurityTrustResourceUrl('assets/audit.svg'));
    this._matIconRegistry.addSvgIcon('folder', this._domSanitizer.bypassSecurityTrustResourceUrl('assets/folder.svg'));
    this._matIconRegistry.addSvgIcon('folder_open', this._domSanitizer.bypassSecurityTrustResourceUrl('assets/folder_open.svg'));

    document.body.parentElement.classList.add(this._settings.theme);
    document.body.classList.add(`bg-${this._settings.backgroundFit}`);
    document.body.style.cssText = `--bg-${this._settings.backgroundType}: ${this._settings.appBackground}`;
  }

  ngOnInit(): void {
    this._keycloak.keycloakEvents$.subscribe((e) => {
      console.log('Keycloak Event:', e.type);
      switch (e.type) {
        // ... other cases ...
        case KeycloakEventType.OnAuthSuccess:
          console.log('AppComponent - OnAuthSuccess');
          this._keycloak.getToken().then((token) => {
            localStorage.setItem('id_token', token);
            console.log('AppComponent - Token stored in localStorage:', localStorage.getItem('id_token'));
          });
          break;
        case KeycloakEventType.OnReady:
          console.log('AppComponent - OnReady');
          break;
        case KeycloakEventType.OnTokenExpired:
          console.log('AppComponent - OnTokenExpired');
          this._keycloak.updateToken(120).then((response) => {
            this._keycloak.getToken().then((token) => {
              console.log('AppComponent - Token refreshed:', token);
              localStorage.setItem('id_token', token);
            });
          });
          break;
      }
    });

    if (ConfigService.apiError) {
      this.router.navigate(['/error']); // Redirect to /error if API unreachable
      return;
    }
    if (!this.initialized) {
      this.router.navigate(['/init']); // Redirect to /init if not initialized
      return;
    }

    this._keycloak.keycloakEvents$.subscribe((e) => {
      switch (e.type) {
        case KeycloakEventType.OnAuthError:
          console.log('OnAuthError');
          break;
        case KeycloakEventType.OnAuthLogout:
          console.log('OnAuthLogout');
          break;
        case KeycloakEventType.OnAuthRefreshError:
          console.log('OnAuthRefreshError');
          // this.logout();
          break;
        case KeycloakEventType.OnAuthRefreshSuccess:
          this._keycloak.getToken().then((token) => {
            localStorage.setItem('id_token', token);
          });
          break;
        case KeycloakEventType.OnAuthSuccess:
          console.log('OnAuthSuccess');
          break;
        case KeycloakEventType.OnReady:
          console.log('OnReady');
          break;
        case KeycloakEventType.OnTokenExpired:
          this._keycloak.updateToken(120).then((response) => {
            this._keycloak.getToken().then((token) => {
              console.log('token refreshed');
              localStorage.setItem('id_token', token);
            });
          });
          break;
      }
    });
  }

  ngAfterViewInit(): void {
    if (!this.initialized) return;

    this._heartbeatService.startHeartbeat();

    // Ensure Claude OAuth token is valid on app load (refreshes if expired)
    console.log('AppComponent: Checking Claude OAuth token validity...');
    this._muxUsage.ensureValidToken().then(valid => {
      console.log('AppComponent: Token check complete, valid:', valid);
    });

    if (this.identity && this.identity.me) {
      this.subs.push(
        this.identity.me$.subscribe((me) => {
          if (!me.selectedTeamName) {
            // In local mode, always use "local" team
            const localMode = window['env']?.['skipAuth'] || window['env']?.['localMode'];
            if (localMode) {
              localStorage.setItem('lastTeamSelected', 'local');
              this.identity.setTeam('local');
            } else {
              const lastTeamSelected = localStorage.getItem('lastTeamSelected');
              if (lastTeamSelected) {
                this.identity.setTeam(lastTeamSelected);
              } else {
                // Auto-select "local" team for new users
                localStorage.setItem('lastTeamSelected', 'local');
                this.identity.setTeam('local');
              }
            }
          }

          if (me.selectedTeamName) {
            this._version.get().then(({ version }) => {
              this.version = version;
            });
          }
        })
      );

      this.subs.push(
        this._teams.load().subscribe((teams) => {
          this.menuItems = teams;

          // Validate that selected team exists in user's team list
          const me = this.identity.me;
          if (me?.selectedTeamName && teams.length > 0) {
            const valid = teams.some(t => (t.slug || t.name) === me.selectedTeamName);
            if (!valid) {
              const first = teams[0];
              const slug = first.slug || first.name;
              console.log(`Selected team "${me.selectedTeamName}" not found in user teams, redirecting to "${slug}"`);
              this.selectTeam(first);
            }
          } else if (!me?.selectedTeamName && teams.length > 0) {
            // No team selected at all — pick the first available
            const first = teams[0];
            const slug = first.slug || first.name;
            console.log(`No team selected, auto-selecting "${slug}"`);
            this.selectTeam(first);
          }
        })
      );

      this.subs.push(
        this.menuService.activeMenuItem$.subscribe((activeMenuItem: string) => {
          this.menuService.activeMenuItem = activeMenuItem;
        })
      );
    }

    this.identity.reload();

    // Sync team preference from server (for cross-device support)
    this._userPrefs.get().subscribe(prefs => {
      if (prefs.preferredTeam && !localStorage.getItem('lastTeamSelected')) {
        localStorage.setItem('lastTeamSelected', prefs.preferredTeam);
      }
    });
  }

  selectTeam(element) {
    if (element.name === '[New Team]') {
      this.router.navigate(['/']);
      return;
    }

    const slug = element.slug || element.name;
    if (this.identity.me.selectedTeamName === slug) return;

    localStorage.setItem('lastTeamSelected', slug);
    this.identity.setTeam(slug);
    this.identity.setProject(null);

    // Save preference server-side for cross-device sync
    this._userPrefs.setPreferredTeam(slug).subscribe();

    this.router.navigate([`/${slug}/grid`]);
  }

  ngOnDestroy(): void {
    this.subs.forEach((x) => x.unsubscribe());
    this._heartbeatService.stopHeartbeat();
  }

  logout() {
    // remove user from local storage to log user out
    localStorage.removeItem('id_token');
    localStorage.removeItem('me');
    this._keycloak.logout(window.location.href);
  }

  navigate(route: string[]) {
    this.menuService.setActive(route[route.length - 1]);
    this.router.navigate(route);
  }

  openSettings() {
    const widget = this._widgets.createWidget('SettingsWidget', this.team?.name ?? '', undefined, {
      defaultSpace: { minItemRows: 16, minItemCols: 27 },
      outputs: {
        onClose: ($event: BaseWidgetComponent.Event) => {
          // use setTimeout, otherwise gridster leaves gridster-preview behind
          setTimeout(() => this._widgets.removeWidget(this.identity.me.selectedTeamName, $event.id));
        },
        onRestore: ($event: BaseWidgetComponent.Event) => {
          this._widgets.restoreWidget(this.identity.me.selectedTeamName, $event.id);
        },
        onMaximize: ($event: BaseWidgetComponent.Event) => {
          this._widgets.maximizeWidget(this.identity.me.selectedTeamName, $event.id);
        },
        onToggle: ($event: BaseWidgetComponent.Event) => {
          this._widgets.toggleWidget(this.identity.me.selectedTeamName, $event.id);
        }
      }
    });
    this._widgets.addWidget(this.identity.me.selectedTeamName, widget);
  }

  openMarkdownViewer(event: { path: string; title: string; startInEditMode?: boolean }) {
    // In chat-page (fullscreen/dockview) mode, the grid isn't active.
    // Relay via WidgetService so the active ChatWidget can open it as an app pane.
    this._widgets.viewMarkdownRequest$.next(event);
  }

  openInit() {
    this.router.navigate(['/init']);
  }

  openUserManager() {
    window.location.href = `${ConfigService.keycloakUrl}/admin/${ConfigService.keycloakRealm}/console/#/${ConfigService.keycloakRealm}/users`;
  }

}
